import React from "react";
import { Upload, X, Music, FileText } from "lucide-react";
import TextField from "@/components/ui/Textfield";
import TextareaField from "@/components/ui/Textareafield";
import CharacterCounter from "@/components/ui/CharacterCounter";
import Checkbox from "@/components/ui/CheckBox";
import Dropdown from "@/components/ui/DropDown";

interface MediaOverride {
  url: string;
  file: File;
}

interface MediaSlot {
  canvas_component_info_id: string;
  url?: string | null;
  original_name?: string | null;
  slot_index: number;
  type?: string;
}

interface ContentItem {
  name: string;
  display_name: string;
  sub_master_id: string;
  saved_value?: string | null;
  settings: {
    character_limit?: number;
    /** Past this the canvas shows "… more". */
    soft_limit?: number | null;
    input_type?: string;
    placeholder?: string;
    label?: string;
    validation?: { type?: string };
    options?: { title: string; value: string }[];
  };
}

interface AdSpecOverview {
  ad_spec_overview: string[];
  ad_spec_values?: Record<string, any>;
}

interface AssetVersion {
  id: string;
  media_json?: Record<string, any> | null;
}

interface ChangelogEntry {
  id: string;
  created_at: string;
  action: string;
  old_value?: { status?: string } | null;
  new_value?: { status?: string } | null;
  changed_by_user?: { first_name: string; last_name: string } | null;
}

/** A single decoded field inside a restricted parameter. */
interface RestrictedParameterField {
  parameter_field_id?: string | null;
  key?: string | null;
  label?: string;
  type?: string;
  options?: { title: string; value: string }[] | string[] | null;
  /** Ad-category configuration; for "Multiple", the allowed value list. */
  value?: unknown;
  /** This version's saved answer, or null if unset. */
  saved_value?: unknown;
}

/** An ad-category parameter the backend flagged as restricted. */
interface RestrictedParameter {
  id: string;
  value_type?: string;
  label?: string;
  parameter_key?: string;
  key?: string;
  fields?: RestrictedParameterField[];
  inputs_json?: Record<string, any> | any[] | null;
  parameter_definition?: {
    id: string;
    key: string;
    label: string;
    input_type: string;
    options?: { title: string; value: string }[] | null;
  } | null;
}

/** Stable key for one field's edit value: parameters can hold several fields. */
export const restrictedFieldKey = (
  parameterId: string,
  fieldKey?: string | null,
) => `${parameterId}::${fieldKey ?? "value"}`;

/**
 * Normalise a configured value list into Dropdown options.
 *
 * For "Multiple" parameters the allowed values live in the field's `value`
 * array, because the ad category stores the list there. `options` is normally
 * null for these (the seeded group-level `extra_meta` is null, so the backend's
 * block builder cannot populate it), which is why `value` is the source here.
 */
const toRestrictedOptions = (
  raw: unknown,
): { title: string; value: string }[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => {
      if (entry != null && typeof entry === "object") {
        const obj = entry as Record<string, any>;
        const value = obj.value ?? obj.id ?? obj.title ?? obj.name;
        const title = obj.title ?? obj.label ?? obj.name ?? value;
        return value == null ? null : { title: String(title), value: String(value) };
      }
      if (entry == null || entry === "") return null;
      return { title: String(entry), value: String(entry) };
    })
    .filter((o): o is { title: string; value: string } => o !== null);
};

/** Map a field's declared type onto an input type the TextField understands. */
const restrictedInputType = (type?: string): string => {
  if (!type) return "text";
  if (type.startsWith("number")) return "number";
  if (type.startsWith("date")) return "date";
  return "text";
};

/**
 * Build the selectable options for a "Multiple" field.
 *
 * The allowed values are the ones the admin configured, held in `field.value`.
 * `field.options` is the field definition's full seeded list, used only to map
 * a stored value onto its display title (e.g. `rgb` → "RGB") so the Ad Builder
 * labels match the Ad Category. Values with no matching definition entry fall
 * back to showing themselves.
 */
const restrictedFieldOptions = (
  field: RestrictedParameterField,
): { title: string; value: string }[] => {
  const titles = new Map(
    toRestrictedOptions(field.options).map((o) => [o.value, o.title]),
  );

  const configured = toRestrictedOptions(field.value);
  const source =
    configured.length > 0 ? configured : toRestrictedOptions(field.options);

  return source.map((o) => ({
    value: o.value,
    title: titles.get(o.value) ?? o.title,
  }));
};

/** Render a configured value for read-only display, preferring option titles. */
const displayRestrictedValue = (
  raw: unknown,
  field?: RestrictedParameterField,
): string => {
  if (raw == null) return "";

  const titles = new Map(
    toRestrictedOptions(field?.options).map((o) => [o.value, o.title]),
  );
  const label = (v: unknown) => titles.get(String(v)) ?? String(v);

  if (Array.isArray(raw)) {
    return raw.filter((v) => v != null && v !== "").map(label).join(", ");
  }
  if (typeof raw === "object") return JSON.stringify(raw);
  return label(raw);
};

interface AdBuilderFormProps {
  adSpecOverview: AdSpecOverview[];
  /** Empty unless the user holds `read_restricted_parameters`. */
  restrictedParameters?: RestrictedParameter[];
  /**
   * Current edit values, keyed by `restrictedFieldKey(parameterId, fieldKey)`.
   * A parameter can expose several fields, so the key cannot be the id alone.
   */
  restrictedValues?: Record<string, string>;
  onRestrictedValueChange?: (valueKey: string, value: string) => void;
  contentItems: ContentItem[];
  objectiveItems: ContentItem[];
  contentValues: Record<string, string>;
  setContentValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  linkAcrossVersions: {
    primaryText: boolean;
    headline: boolean;
    description: boolean;
  };
  setLinkAcrossVersions: React.Dispatch<
    React.SetStateAction<{
      primaryText: boolean;
      headline: boolean;
      description: boolean;
    }>
  >;
  urlErrors: Record<string, string>;
  setUrlErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  mediaSlots: MediaSlot[];
  mediaComponentType: string | null | undefined;
  isVideoMedia?: boolean;
  mediaOverrides: Record<string, MediaOverride>;
  activeVersionId: string | null;
  assetVersions: AssetVersion[];
  /**
   * `slotIndex` is required for multi-slot components (carousels, playlists):
   * without it every slot shares one override key and an upload to one card
   * replaces the image on all of them.
   */
  onMediaSelect: (
    componentInfoId: string,
    file: File,
    slotIndex?: number,
  ) => void;
  onMediaClear: (componentInfoId: string, slotIndex?: number) => void;
  /** Field names the template locked (Properties → Editable off). */
  lockedFieldNames: Set<string>;
  /** Canvas component ids whose media the template locked. */
  lockedMediaComponentIds: Set<string>;
  /** Where each media element sits on the canvas, keyed by component id. */
  mediaSlotDescriptions: Record<string, { label: string; position: string }>;
  changelog: ChangelogEntry[];
  isChangelogLoading: boolean;
}

const AdBuilderForm: React.FC<AdBuilderFormProps> = ({
  adSpecOverview,
  restrictedParameters = [],
  restrictedValues = {},
  onRestrictedValueChange,
  contentItems,
  objectiveItems,
  contentValues,
  setContentValues,
  linkAcrossVersions,
  setLinkAcrossVersions,
  urlErrors,
  setUrlErrors,
  mediaSlots,
  mediaComponentType,
  isVideoMedia,
  mediaOverrides,
  activeVersionId,
  assetVersions,
  onMediaSelect,
  onMediaClear,
  lockedFieldNames,
  lockedMediaComponentIds,
  mediaSlotDescriptions,
  changelog,
  isChangelogLoading,
}) => {
  return (
    <div className="space-y-6 mt-14">
      {/* Ad Specs */}
      {(adSpecOverview?.length > 0 || restrictedParameters.length > 0) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Ad Specs</h2>
          </div>
          {adSpecOverview?.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {adSpecOverview[0].ad_spec_overview.map((spec) => {
                const val = adSpecOverview[0].ad_spec_values?.[spec];
                return (
                  <span
                    key={spec}
                    className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700 capitalize"
                  >
                    {spec.replace(/_/g, " ")}{val ? `: ${val}` : ""}
                  </span>
                );
              })}
            </div>
          )}

          {/* Restricted parameters render inline with the other specs, with no
              special marking. The list is empty unless the user holds
              `read_restricted_parameters`, so no permission check is needed. */}
          {restrictedParameters.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              {restrictedParameters.flatMap((param) => {
                const def = param.parameter_definition;
                const groupLabel = param.label ?? def?.label ?? "";

                // The backend sends decoded per-field specs. Most of these
                // parameters are `input_type: "group"`, so the group-level
                // input_type cannot be used to choose a control — each field
                // gets its own, driven by the parameter's value_type.
                const fields: RestrictedParameterField[] =
                  param.fields && param.fields.length > 0
                    ? param.fields
                    : // Fall back to a single unnamed field so an unexpected
                      // payload still renders something editable.
                      [{ key: def?.key ?? null, label: groupLabel, type: "text" }];

                const multiField = fields.length > 1;

                return fields.map((field) => {
                  const valueKey = restrictedFieldKey(param.id, field.key);
                  const current = restrictedValues[valueKey] ?? "";
                  const fieldLabel = multiField
                    ? `${groupLabel} — ${field.label ?? field.key}`
                    : groupLabel || field.label || field.key || "";

                  // Same control logic as the Assets page:
                  //  multi_publisher  → pick one of the configured values
                  //  user_defined     → filled in per asset, so editable
                  //  single_publisher → fixed by the ad category, so read-only
                  let control: React.ReactNode;

                  if (param.value_type === "multi_publisher") {
                    const options = restrictedFieldOptions(field);

                    control = (
                      <Dropdown
                        options={options}
                        selected={current}
                        setSelected={(val) =>
                          onRestrictedValueChange?.(valueKey, val.toString())
                        }
                        className="w-full"
                        placeholder={
                          options.length > 0
                            ? "Select option..."
                            : "No values configured"
                        }
                        disabled={options.length === 0}
                      />
                    );
                  } else if (param.value_type === "user_defined") {
                    control = (
                      <TextField
                        type={restrictedInputType(field.type)}
                        value={current}
                        onChange={(e) =>
                          onRestrictedValueChange?.(valueKey, e.target.value)
                        }
                        placeholder={`Enter ${(field.label ?? fieldLabel).toLowerCase()}`}
                        className="w-full"
                      />
                    );
                  } else {
                    // Default: the ad category fixed this value, so it is shown
                    // for reference rather than edited here.
                    control = (
                      <TextField
                        type="text"
                        value={displayRestrictedValue(field.value, field)}
                        disabled
                        className="w-full bg-gray-100"
                      />
                    );
                  }

                  return (
                    <div key={valueKey}>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        {fieldLabel}
                      </label>
                      {control}
                    </div>
                  );
                });
              })}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {contentItems.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Content</h2>
          <hr className="border-gray-300 mb-5" />
          <div className="grid grid-cols-2 gap-4">
            {contentItems.map((item, idx) => {
              const charLimit = item.settings.character_limit;
              const inputType = item.settings.input_type;
              const placeholder =
                item.settings.placeholder || "Placeholder text....";
              const label = item.settings.label || item.display_name;
              const fieldKey = item.name;
              const value = contentValues[fieldKey] ?? "";
              // Locked by the template: shown, but fixed and not editable.
              const isLocked = lockedFieldNames.has(fieldKey);

              return (
                <div key={`${item.sub_master_id}-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-900">
                      {label}
                    </label>
                    {!isLocked && (
                      <label className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="text-xs">Link across versions</span>
                        <Checkbox
                          checked={linkAcrossVersions.headline}
                          onChange={(e) =>
                            setLinkAcrossVersions((prev) => ({
                              ...prev,
                              headline: e,
                            }))
                          }
                        />
                      </label>
                    )}
                  </div>
                  {inputType === "textarea" ? (
                    <TextareaField
                      value={value}
                      onChange={(e) =>
                        setContentValues((prev) => ({
                          ...prev,
                          [fieldKey]: e.target.value,
                        }))
                      }
                      placeholder={placeholder}
                      rows={4}
                      maxLength={charLimit || undefined}
                      disabled={isLocked}
                      className="w-full"
                    />
                  ) : (
                    <TextField
                      type="text"
                      value={value}
                      onChange={(e) =>
                        setContentValues((prev) => ({
                          ...prev,
                          [fieldKey]: e.target.value,
                        }))
                      }
                      placeholder={placeholder}
                      maxLength={charLimit || undefined}
                      disabled={isLocked}
                      className="w-full"
                    />
                  )}
                  <CharacterCounter
                    length={value.length}
                    limit={charLimit}
                    softLimit={item.settings.soft_limit}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Objectives */}
      {objectiveItems.length > 0 && (
        <div className="mt-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Objectives
          </h2>
          <hr className="border-gray-300 mb-5" />
          <div className="grid grid-cols-2 gap-4">
            {objectiveItems.map((item) => {
              const fieldKey = item.name;
              const value = contentValues[fieldKey] ?? item.saved_value ?? "";
              const inputType = item.settings.input_type;
              const isUrl =
                (item.settings as any).validation?.type === "url";
              const options = (item.settings as any).options as
                | { title: string; value: string }[]
                | undefined;
              const isLocked = lockedFieldNames.has(fieldKey);
              return (
                <div key={item.sub_master_id}>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {item.settings.label || item.display_name}
                  </label>
                  {inputType === "select" && options ? (
                    <div className={isLocked ? "pointer-events-none opacity-60" : ""}>
                      <Dropdown
                        selected={value}
                        setSelected={(val: string) =>
                          setContentValues((prev) => ({
                            ...prev,
                            [fieldKey]: val,
                          }))
                        }
                        options={options}
                        placeholder={
                          item.settings.placeholder || "Select option"
                        }
                        className="w-full"
                      />
                    </div>
                  ) : (
                    <div
                      onBlur={() => {
                        if (!isUrl || !value) return;
                        try {
                          new URL(value);
                          setUrlErrors((prev) => ({
                            ...prev,
                            [fieldKey]: "",
                          }));
                        } catch {
                          setUrlErrors((prev) => ({
                            ...prev,
                            [fieldKey]: "Please enter a valid URL",
                          }));
                        }
                      }}
                    >
                      <TextField
                        type="text"
                        value={value}
                        onChange={(e) => {
                          setContentValues((prev) => ({
                            ...prev,
                            [fieldKey]: e.target.value,
                          }));
                          if (urlErrors[fieldKey])
                            setUrlErrors((prev) => ({
                              ...prev,
                              [fieldKey]: "",
                            }));
                        }}
                        placeholder={item.settings.placeholder || ""}
                        disabled={isLocked}
                        className="w-full"
                      />
                      {urlErrors[fieldKey] && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors[fieldKey]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Media */}
      {mediaSlots.length > 0 && (
        <div>
          <div className="text-sm font-medium text-gray-900 mb-1">Media</div>
          <p className="text-xs text-gray-500 mb-3">
            Each box below is one area of the ad preview above. The caption
            shows where it appears and what size to upload.
          </p>
          {(() => {
            const mediaType = mediaComponentType?.toLowerCase() ?? "";
            /** Caption describing where a slot's element sits on the canvas. */
            const describeSlot = (componentInfoId: string) =>
              mediaSlotDescriptions[componentInfoId];
            // The profile picture is image-backed but is not part of the
            // creative, so it is pulled out and rendered as its own labelled
            // slot. Counting it as a creative slot made a 5-card carousel
            // report "6 cards" and numbered the avatar as the last card.
            const avatarSlots = mediaSlots.filter((s) => s.type === "avatar");
            const creativeSlots = mediaSlots.filter((s) => s.type !== "avatar");
            const isCarousel = creativeSlots.length > 1;

            /**
             * The profile picture, rendered below whichever creative layout
             * applies. Shared so every branch shows it the same way.
             */
            const renderAvatarSlots = () => {
              if (avatarSlots.length === 0) return null;
              return (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  {avatarSlots.map((slot) => {
                    const key = `${slot.canvas_component_info_id}_${slot.slot_index}`;
                    const override = mediaOverrides[key];
                    const savedUrl =
                      (
                        assetVersions.find((v) => v.id === activeVersionId)
                          ?.media_json?.[key] as any
                      )?.url ?? null;
                    const previewUrl = override?.url ?? slot.url ?? savedUrl;
                    const desc = describeSlot(slot.canvas_component_info_id);
                    const locked = lockedMediaComponentIds.has(
                      slot.canvas_component_info_id,
                    );

                    return (
                      <div
                        key={key}
                        className="flex items-center gap-3"
                      >
                        <label
                          className={`relative w-16 h-16 rounded-full overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0 ${
                            // Solid ring once filled, dashed while empty, so an
                            // uploaded avatar does not keep looking like a
                            // drop target.
                            previewUrl
                              ? "border border-gray-200"
                              : "border-2 border-dashed border-gray-300"
                          } ${
                            locked
                              ? "cursor-not-allowed opacity-40"
                              : "cursor-pointer hover:bg-gray-100"
                          }`}
                        >
                          {previewUrl ? (
                            // Sized explicitly rather than with w-full/h-full:
                            // as a flex child the image was being shrunk to its
                            // intrinsic ratio instead of filling the circle.
                            <img
                              src={previewUrl}
                              alt="Profile"
                              className="w-16 h-16 object-cover rounded-full"
                            />
                          ) : (
                            <span className="text-2xl text-gray-300 font-light">
                              +
                            </span>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={locked}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file)
                                onMediaSelect(
                                  slot.canvas_component_info_id,
                                  file,
                                  slot.slot_index,
                                );
                            }}
                          />
                        </label>
                        <div className="min-w-0">
                          <div className="text-sm text-gray-700 font-medium">
                            {desc?.label ?? "Profile Picture"}
                          </div>
                          {desc?.position && (
                            <div className="text-xs text-gray-500">
                              {desc.position}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-0.5">
                            Account picture in the ad header. Use a square
                            image, cropped to a circle.
                          </div>
                        </div>
                        {override && !locked && (
                          <button
                            onClick={() =>
                              onMediaClear(
                                slot.canvas_component_info_id,
                                slot.slot_index,
                              )
                            }
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            };
            const isAudio = mediaType === "audio";
            const isDocument =
              mediaType === "document" || mediaType === "pdf";

            // ── Audio slots ──────────────────────────────────────
            if (isAudio) {
              return (
                <div className="space-y-3">
                  {(() => {
                    const desc = describeSlot(
                      creativeSlots[0]?.canvas_component_info_id,
                    );
                    return (
                      <div className="mb-1">
                        <div className="text-sm text-gray-700 font-medium">
                          {desc?.label ??
                            (creativeSlots.length > 1
                              ? `${creativeSlots.length} Audio Tracks`
                              : "Audio File")}
                        </div>
                        <div className="text-xs text-gray-500">
                          {creativeSlots.length > 1
                            ? `${creativeSlots.length} tracks`
                            : "Audio file"}
                          {desc?.position ? ` · ${desc.position}` : ""}
                        </div>
                      </div>
                    );
                  })()}
                  {creativeSlots.map((slot, idx) => {
                    // Keyed on the slot's own index, matching what the upload
                    // handler writes. Using the array position instead broke
                    // whenever slots arrived out of order or sparsely.
                    const overrideKey = `${slot.canvas_component_info_id}_${slot.slot_index}`;
                    const override = mediaOverrides[overrideKey];
                    const audioUrl = override?.url ?? slot.url;
                    return (
                      <div
                        key={slot.slot_index}
                        className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg"
                      >
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Music size={14} className="text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            {creativeSlots.length > 1
                              ? `Track ${idx + 1}`
                              : "Audio"}
                          </div>
                          {audioUrl ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-purple-200 rounded-full">
                                <div className="h-full w-0 bg-purple-500 rounded-full" />
                              </div>
                              <span className="text-[10px] text-gray-500 truncate max-w-[80px]">
                                {override?.file?.name ??
                                  slot.original_name ??
                                  "Uploaded"}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[10px] text-gray-400">
                              No file uploaded
                            </span>
                          )}
                        </div>
                        <label
                          className={
                            lockedMediaComponentIds.has(
                              slot.canvas_component_info_id,
                            )
                              ? "cursor-not-allowed opacity-40"
                              : "cursor-pointer"
                          }
                        >
                          <Upload
                            size={14}
                            className="text-purple-400 hover:text-purple-600"
                          />
                          <input
                            type="file"
                            accept="audio/*,.mp3,.wav,.ogg,.aac,.m4a"
                            className="hidden"
                            disabled={lockedMediaComponentIds.has(
                              slot.canvas_component_info_id,
                            )}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file)
                                onMediaSelect(
                                  slot.canvas_component_info_id,
                                  file,
                                  slot.slot_index,
                                );
                            }}
                          />
                        </label>
                        {override &&
                          !lockedMediaComponentIds.has(
                            slot.canvas_component_info_id,
                          ) && (
                            <button
                              onClick={() =>
                                onMediaClear(
                                  slot.canvas_component_info_id,
                                  slot.slot_index,
                                )
                              }
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X size={14} />
                            </button>
                          )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            // ── Document / PDF slots ────────────────────────────
            if (isDocument) {
              return (
                <div className="space-y-3">
                  {(() => {
                    const desc = describeSlot(
                      creativeSlots[0]?.canvas_component_info_id,
                    );
                    return (
                      <div className="mb-1">
                        <div className="text-sm text-gray-700 font-medium">
                          {desc?.label ?? "Document"}
                        </div>
                        {desc?.position && (
                          <div className="text-xs text-gray-500">
                            {desc.position}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {creativeSlots.map((slot, idx) => {
                    // Slot-qualified to match the upload handler's key.
                    const override =
                      mediaOverrides[
                        `${slot.canvas_component_info_id}_${slot.slot_index}`
                      ];
                    const docUrl = override?.url ?? slot.url;
                    return (
                      <div
                        key={slot.slot_index}
                        className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg"
                      >
                        <FileText
                          size={20}
                          className="text-amber-500 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-700">
                            {mediaType === "pdf"
                              ? "PDF"
                              : `Document ${idx + 1}`}
                          </div>
                          {docUrl ? (
                            <span className="text-[10px] text-gray-500 truncate block">
                              {override?.file?.name ??
                                slot.original_name ??
                                "Uploaded"}
                            </span>
                          ) : (
                            <span className="text-[10px] text-gray-400">
                              No file uploaded
                            </span>
                          )}
                        </div>
                        <label
                          className={
                            lockedMediaComponentIds.has(
                              slot.canvas_component_info_id,
                            )
                              ? "cursor-not-allowed opacity-40"
                              : "cursor-pointer"
                          }
                        >
                          <Upload
                            size={14}
                            className="text-amber-400 hover:text-amber-600"
                          />
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md"
                            className="hidden"
                            disabled={lockedMediaComponentIds.has(
                              slot.canvas_component_info_id,
                            )}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file)
                                onMediaSelect(
                                  slot.canvas_component_info_id,
                                  file,
                                  slot.slot_index,
                                );
                            }}
                          />
                        </label>
                        {override &&
                          !lockedMediaComponentIds.has(
                            slot.canvas_component_info_id,
                          ) && (
                            <button
                              onClick={() =>
                                onMediaClear(
                                  slot.canvas_component_info_id,
                                  slot.slot_index,
                                )
                              }
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X size={14} />
                            </button>
                          )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            // ── Carousel image/video strip ───────────────────────
            if (isCarousel) {
              // Determine if carousel holds video based on slot type or canvas component master
              const isVideoCarousel =
                isVideoMedia || creativeSlots.some((s) => s.type === "video");
              return (
                <div>
                  {(() => {
                    const desc = describeSlot(
                      creativeSlots[0]?.canvas_component_info_id,
                    );
                    return (
                      <div className="mb-3">
                        <div className="text-sm text-gray-700 font-medium">
                          {desc?.label ??
                            (isVideoCarousel ? "Video Carousel" : "Image Carousel")}
                        </div>
                        <div className="text-xs text-gray-500">
                          {creativeSlots.length}{" "}
                          {isVideoCarousel ? "clips" : "cards"}
                          {desc?.position ? ` · ${desc.position}` : ""}
                        </div>
                        {/* Carousels are the one place where slot order is
                            user-visible, so it is worth stating outright. */}
                        <div className="text-xs text-gray-400 mt-0.5">
                          Shown in order. Empty cards are skipped.
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {creativeSlots.map((slot, idx) => {
                      // Overrides for a multi-slot component are keyed per slot,
                      // so each card keeps its own upload instead of all cards
                      // sharing (and overwriting) a single entry.
                      const override =
                        mediaOverrides[
                          `${slot.canvas_component_info_id}_${slot.slot_index}`
                        ];
                      // Saved uploads come back on the asset version's
                      // media_json under the same per-slot key; without this the
                      // thumbnail was empty again after a reload.
                      const savedUrl =
                        (
                          assetVersions.find((v) => v.id === activeVersionId)
                            ?.media_json?.[
                            `${slot.canvas_component_info_id}_${slot.slot_index}`
                          ] as any
                        )?.url ?? null;
                      const previewUrl = override?.url ?? slot.url ?? savedUrl;
                      const slotIsVideo = isVideoCarousel || slot.type === "video";
                      return (
                        <div
                          key={slot.slot_index}
                          className="flex-shrink-0 relative"
                        >
                          <div className="text-xs text-gray-500 text-center font-medium">
                            {isVideoCarousel ? `Clip ${idx + 1}` : `Card ${idx + 1}`}
                          </div>
                          {/* Filled/empty at a glance, so a missed card is
                              obvious before the ad is submitted. */}
                          <div
                            className={`text-[10px] mb-1 text-center ${
                              previewUrl ? "text-green-600" : "text-gray-400"
                            }`}
                          >
                            {previewUrl ? "Uploaded" : "Empty"}
                          </div>
                          {previewUrl ? (
                            <div className="relative w-28 h-28">
                              {slotIsVideo ? (
                                <video
                                  src={previewUrl}
                                  className="w-28 h-28 object-cover rounded-lg border border-gray-200"
                                  muted
                                />
                              ) : (
                                <img
                                  src={previewUrl}
                                  alt={`Card ${idx + 1}`}
                                  className="w-28 h-28 object-cover rounded-lg border border-gray-200"
                                />
                              )}
                              {override &&
                                !lockedMediaComponentIds.has(
                                  slot.canvas_component_info_id,
                                ) && (
                                  <button
                                    onClick={() =>
                                      onMediaClear(
                                        slot.canvas_component_info_id,
                                        slot.slot_index,
                                      )
                                    }
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"
                                  >
                                    <X size={10} />
                                  </button>
                                )}
                              <label
                                className={`absolute bottom-1 right-1 rounded p-0.5 transition-colors ${
                                  lockedMediaComponentIds.has(
                                    slot.canvas_component_info_id,
                                  )
                                    ? "bg-white/50 cursor-not-allowed opacity-40"
                                    : "bg-white/80 cursor-pointer hover:bg-white"
                                }`}
                                title="Replace"
                              >
                                <Upload size={12} className="text-gray-600" />
                                <input
                                  type="file"
                                  accept={
                                    slotIsVideo
                                      ? "video/*"
                                      : "image/*"
                                  }
                                  className="hidden"
                                  disabled={lockedMediaComponentIds.has(
                                    slot.canvas_component_info_id,
                                  )}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file)
                                      onMediaSelect(
                                        slot.canvas_component_info_id,
                                        file,
                                        slot.slot_index,
                                      );
                                  }}
                                />
                              </label>
                            </div>
                          ) : (
                            <label
                              className={`w-28 h-28 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center bg-gray-50 transition-colors ${
                                lockedMediaComponentIds.has(
                                  slot.canvas_component_info_id,
                                )
                                  ? "cursor-not-allowed opacity-40"
                                  : "hover:bg-gray-100 cursor-pointer"
                              }`}
                            >
                              <div className="text-2xl text-gray-300 font-light mb-1">
                                +
                              </div>
                              <span className="text-[10px] text-gray-400 capitalize text-center px-1">
                                {slotIsVideo ? "Video" : "Image"} (30MB)
                              </span>
                              <input
                                type="file"
                                accept={
                                  slotIsVideo
                                    ? "video/*"
                                    : "image/*"
                                }
                                className="hidden"
                                disabled={lockedMediaComponentIds.has(
                                  slot.canvas_component_info_id,
                                )}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file)
                                    onMediaSelect(
                                      slot.canvas_component_info_id,
                                      file,
                                      slot.slot_index,
                                    );
                                }}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {renderAvatarSlots()}
                </div>
              );
            }

            // ── Single image/video slot ──────────────────────────
            return (
              <div className="flex items-start gap-3 flex-wrap">
                {creativeSlots.map((slot) => {
                  const override =
                    mediaOverrides[slot.canvas_component_info_id];
                  const activeVersion = assetVersions.find(
                    (v) => v.id === activeVersionId,
                  );
                  const mediaJsonEntry = activeVersion?.media_json?.[
                    slot.canvas_component_info_id
                  ] as any;
                  const mediaJsonUrl = mediaJsonEntry?.url ?? null;
                  const mediaJsonName =
                    mediaJsonEntry?.original_name ?? null;
                  const mediaJsonMime = mediaJsonEntry?.mime_type ?? null;
                  const previewUrl =
                    override?.url ?? mediaJsonUrl ?? slot.url;
                  const previewName =
                    override?.file?.name ??
                    mediaJsonName ??
                    slot.original_name ??
                    null;
                  const isVideo = override
                    ? override.file.type.startsWith("video/")
                    : mediaJsonMime
                      ? mediaJsonMime.startsWith("video/")
                      : slot.type === "video" || isVideoMedia;
                  const desc = describeSlot(slot.canvas_component_info_id);
                  return (
                    <div key={slot.slot_index} className="relative">
                      <div className="mb-2">
                        <div className="text-xs font-medium text-gray-700">
                          {desc?.label ?? `Slot ${slot.slot_index + 1}`}
                        </div>
                        {desc?.position && (
                          <div className="text-[10px] text-gray-500">
                            {desc.position}
                          </div>
                        )}
                      </div>
                      {previewUrl ? (
                        <div className="relative w-32 h-32">
                          {isVideo ? (
                            <video
                              src={previewUrl}
                              className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                              muted
                            />
                          ) : (
                            <img
                              src={previewUrl}
                              alt={previewName ?? "media"}
                              className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                            />
                          )}
                          {previewName && (
                            <div className="absolute bottom-6 left-0 right-0 text-center">
                              <span className="text-[9px] text-white bg-black/50 px-1 py-0.5 rounded truncate block mx-1">
                                {previewName}
                              </span>
                            </div>
                          )}
                          {override && (
                            <button
                              onClick={() =>
                                onMediaClear(slot.canvas_component_info_id)
                              }
                              className="absolute -top-2 -right-2 w-5 h-5 bg-gray-700 text-white rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"
                            >
                              <X size={10} />
                            </button>
                          )}
                          <label
                            className="absolute bottom-1 right-1 cursor-pointer bg-white/80 rounded p-0.5 hover:bg-white transition-colors"
                            title="Replace"
                          >
                            <Upload size={12} className="text-gray-600" />
                            <input
                              type="file"
                              accept={isVideo ? "video/*" : "image/*"}
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file)
                                  onMediaSelect(
                                    slot.canvas_component_info_id,
                                    file,
                                  );
                              }}
                            />
                          </label>
                        </div>
                      ) : (
                        <label className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
                          <svg
                            className="w-8 h-8 text-gray-400 mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                          <span className="text-xs text-gray-500 capitalize">
                            {slot.type} (30MB max)
                          </span>
                          <input
                            type="file"
                            accept={
                              slot.type === "video" ? "video/*" : "image/*"
                            }
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file)
                                onMediaSelect(
                                  slot.canvas_component_info_id,
                                  file,
                                );
                            }}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
                {/* Full width so the avatar sits on its own row below the
                    creative rather than wrapping in beside it. */}
                <div className="w-full">{renderAvatarSlots()}</div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Change log */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Change Log
        </h2>
        <hr className="border-gray-300 mb-5" />

        {isChangelogLoading ? (
          <div className="text-sm text-gray-400">Loading...</div>
        ) : changelog.length === 0 ? (
          <div className="text-sm text-gray-400">
            No changes recorded yet.
          </div>
        ) : (
          <div className="space-y-3">
            {changelog.map((entry) => {
              const date = new Date(entry.created_at);
              const formatted =
                date.toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                }) +
                " " +
                date.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                });
              const actionLabel = entry.action.replace(/_/g, " ");
              const userName = entry.changed_by_user
                ? `${entry.changed_by_user.first_name} ${entry.changed_by_user.last_name}`.trim()
                : null;
              const detail =
                entry.action === "status_changed"
                  ? `${entry.old_value?.status ?? "—"} → ${entry.new_value?.status ?? "—"}`
                  : null;
              return (
                <div
                  key={entry.id}
                  className="flex items-start gap-3 text-sm"
                >
                  <div className="text-gray-400 whitespace-nowrap shrink-0">
                    {formatted}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-gray-700 capitalize font-medium">
                      {actionLabel}
                    </span>
                    {detail && (
                      <span className="text-gray-400 text-xs">{detail}</span>
                    )}
                    {userName && (
                      <span className="text-gray-400 text-xs">
                        User: {userName}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdBuilderForm;
